import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { User, Company } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const userSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres').optional().or(z.literal('')),
  role: z.enum(['superadmin', 'company_admin']),
  companyId: z.string().optional(),
}).refine((data) => {
  // Si es company_admin, debe tener empresa
  if (data.role === 'company_admin' && !data.companyId) {
    return false;
  }
  return true;
}, {
  message: 'Los administradores de empresa deben estar vinculados a una empresa',
  path: ['companyId'],
});

type UserFormData = z.infer<typeof userSchema>;

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: User;
  companies?: Company[];
  onSubmit: (data: UserFormData) => void;
  isLoading?: boolean;
}

const roleLabels = {
  superadmin: 'Super Admin',
  company_admin: 'Admin de Empresa',
};

interface NativeSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}

function NativeSelect({ value, onChange, placeholder, options, className, disabled }: NativeSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer",
          !value && "text-muted-foreground",
          className
        )}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
}

export function UserFormDialog({
  open,
  onOpenChange,
  user,
  companies = [],
  onSubmit,
  isLoading = false,
}: UserFormDialogProps) {
  const { isSuperAdmin } = useAuth();
  const isEditing = !!user;
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'company_admin',
      companyId: '',
    },
  });

  const selectedRole = form.watch('role');

  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        email: user.email,
        password: '', // No mostramos la contraseña al editar
        role: user.role,
        companyId: user.companyId || '',
      });
    } else {
      form.reset({
        name: '',
        email: '',
        password: '',
        role: 'company_admin',
        companyId: '',
      });
    }
  }, [user, form, open]);

  // Si cambia a superadmin, limpiar empresa
  useEffect(() => {
    if (selectedRole === 'superadmin') {
      form.setValue('companyId', '');
    }
  }, [selectedRole, form]);

  const handleSubmit = (data: UserFormData) => {
    onSubmit(data);
  };

  // Solo superadmin puede crear otros superadmins
  const availableRoles = isSuperAdmin
    ? ['superadmin', 'company_admin'] as const
    : ['company_admin'] as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica los datos del usuario.'
              : 'Rellena los datos para crear un nuevo usuario.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Nombre completo" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="email@empresa.com" 
                      type="email" 
                      {...field} 
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{isEditing ? 'Nueva Contraseña' : 'Contraseña *'}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input 
                        placeholder={isEditing ? 'Dejar en blanco para mantener' : 'Mínimo 6 caracteres'}
                        type={showPassword ? 'text' : 'password'} 
                        {...field} 
                        disabled={isLoading}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  {isEditing && (
                    <FormDescription>
                      Deja en blanco si no deseas cambiar la contraseña
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rol *</FormLabel>
                  <FormControl>
                    <NativeSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Selecciona un rol"
                      disabled={isLoading}
                      options={availableRoles.map((role) => ({
                        value: role,
                        label: roleLabels[role],
                      }))}
                    />
                  </FormControl>
                  <FormDescription>
                    {selectedRole === 'superadmin' && 'Acceso total a todas las empresas y funcionalidades'}
                    {selectedRole === 'company_admin' && 'Administración completa de la empresa asignada'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedRole === 'company_admin' && (
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empresa *</FormLabel>
                    <FormControl>
                      <NativeSelect
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Selecciona una empresa"
                        disabled={isLoading}
                        options={
                          companies.length === 0
                            ? [{ value: '', label: 'No hay empresas disponibles' }]
                            : companies.map((company) => ({
                                value: company.id,
                                label: company.name,
                              }))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditing ? 'Guardando...' : 'Creando...'}
                  </>
                ) : (
                  isEditing ? 'Guardar cambios' : 'Crear usuario'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
